import React from "react";
import { StyleSheet, View } from "react-native";
import { Controller, Control } from "react-hook-form";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomRichTextEditor from "../../../components/TextEditor/CustomRichTextEditor";

interface AddCourseSecondStepProps {
  control: Control;
  setValue: (name: string, value: string, options?: { shouldDirty: boolean; shouldValidate: boolean }) => void;
  activelyEditing: boolean;
  setActivelyEditing: (activelyEditing: boolean) => void;
}

const AddCourseSecondStep: React.FC<AddCourseSecondStepProps> = ({
  control,
  setValue,
  activelyEditing,
  setActivelyEditing,
}) => {
  return (
    <>
      <View style={styles.formGroup}>
        <CustomText
          text="Course Description"
          size="s"
          textType="text"
          style={{ color: "#182230" }}
          weight="ultraBold"
        />
        <CustomText
          text="This information appears on the course details page in the course catalogue."
          size="xs"
          style={styles.helperText}
          useTranslationText
        />
        <Controller
          control={control}
          name="description"
          render={({ field: { onChange, value } }) => (
            <CustomTextInput
              placeholder="e.g. Discover the world of Artificial Intelligence (AI) and its transformative impact on industries and everyday life. This course introduces the fundamental concepts of AI, including machine learning, natural language processing, robotics, and computer vision. Through hands-on activities and real-world examples, you'll gain practical knowledge of how AI systems are built, trained, and deployed."
              multiline
              numberOfLines={10}
              value={value}
              onChangeText={onChange}
              aria-label="Course description text input"
              style={styles.input}
              placeholderTextColor="#98A2B3"
            />
          )}
        />
      </View>
      <View style={styles.formGroup}>
        <CustomText
          text="What will the student learn in this course?"
          size="s"
          textType="text"
          style={{ color: "#182230" }}
          weight="ultraBold"
        />
        <CustomText
          text="This information appears on the course details page in the course catalogue and is best formatted by using bullets."
          size="xs"
          style={styles.helperText}
          useTranslationText
        />
        <Controller
          control={control}
          name="objectives"
          render={({ field: { onChange, value } }) => {
            const handleOnChange = (content: string) => {
              setActivelyEditing(true);
              onChange(content);
              setValue("objectives", content, { shouldDirty: true, shouldValidate: false });
            };

            return (
              <CustomRichTextEditor
                value={value}
                onChange={handleOnChange}
                isSubmitting={false}
                placeholder="Add a description of what students will learn in this course"
                activelyEditing={activelyEditing}
                restrictImageUpload={true}
              />
            );
          }}
        />
      </View>
      <View style={styles.formGroup}>
        <CustomText
          text="Who is this course for?"
          size="s"
          textType="text"
          style={{ color: "#182230" }}
          weight="ultraBold"
        />
        <CustomText
          text="This information appears on the course details page in the course catalogue."
          size="xs"
          style={styles.helperText}
          useTranslationText
        />
        <Controller
          control={control}
          name="targetAudience"
          render={({ field: { onChange, value } }) => (
            <CustomTextInput
              placeholder="e.g. Students interested in technology, data science, and future-focused careers. Perfect for beginners looking to explore AI without prior technical expertise."
              multiline
              numberOfLines={5}
              value={value}
              onChangeText={onChange}
              aria-label="Target audience text input"
              style={styles.input}
              placeholderTextColor="#98A2B3"
            />
          )}
        />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 24,
  },
  helperText: {
    color: "#6B7280",
    marginBottom: 8,
  },
  input: {
    width: "100%",
    fontSize: 16,
    borderRadius: 10,
    fontWeight: "400",
  },
});

export default AddCourseSecondStep;
